import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";

import { register, resendVerification } from "../../api/auth";
import AuthContext from "../../contexts/AuthCtx";
import AuthLayout from "../../components/UX/AuthLayout";
import Button from "../../components/UX/Button";
import GoogleAuthButton from "../../components/UX/GoogleAuthButton";
import PasswordInput from "../../components/UX/PasswordInput";
import TextInput from "../../components/UX/TextInput";
import { getPostAuthPath } from "../../utils/authRedirect";

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function Register() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (AuthCtx.isLoggedIn) {
      navigate(getPostAuthPath(AuthCtx.userStatus), { replace: true });
    }
  }, [AuthCtx.isLoggedIn, AuthCtx.userStatus, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!emailPattern.test(email.toLowerCase())) {
      toast.error("Veuillez saisir une adresse e-mail valide.");
      return;
    }

    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setLoading(true);
    try {
      const response = await register(email.toLowerCase(), password);
      if (response.data?.requiresEmailVerification) {
        setRegisteredEmail(email.toLowerCase());
        setAwaitingVerification(true);
        return;
      }
      toast.error("Réponse inattendue du serveur. Veuillez réessayer.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Impossible de créer ton compte.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registeredEmail || resending) return;
    setResending(true);
    try {
      await resendVerification(registeredEmail);
      toast.success("Un nouvel e-mail de confirmation a été envoyé.");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Impossible de renvoyer l'e-mail. Réessaie dans quelques minutes."
      );
    } finally {
      setResending(false);
    }
  };

  if (awaitingVerification) {
    return (
      <AuthLayout
        headerLink={
          <>
            <Link to="/login" className="text-primaryText underline">
              Se connecter
            </Link>
          </>
        }
      >
        <div className="text-center flex flex-col gap-4">
          <h1 className="font-logo text-3xl md:text-4xl text-black">
            Confirme ton e-mail
          </h1>
          <p className="font-josefin text-secondaryText text-sm md:text-base">
            Nous avons envoyé un lien d&apos;activation à{" "}
            <span className="text-primaryText font-medium">{registeredEmail}</span>.
            Clique sur le lien pour accéder à ton espace (valable 24 heures).
          </p>
          <p className="font-josefin text-sm text-secondaryText">
            Tu n&apos;as rien reçu ? Vérifie tes spams, puis{" "}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-primaryText underline disabled:opacity-50"
            >
              {resending ? "Envoi…" : "renvoie l'e-mail de confirmation"}
            </button>
            {" "}
            ou{" "}
            <Link to="/login" className="text-primaryText underline">
              connecte-toi
            </Link>{" "}
            une fois ton e-mail confirmé.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      headerLink={
        <>
          Tu as déjà un compte ?{" "}
          <Link to="/login" className="text-primaryText underline">
            Se connecter
          </Link>
        </>
      }
    >
      <div className="text-center mb-2">
        <h1 className="font-logo text-3xl md:text-4xl text-black mb-3">
          Crée ton espace personnel
        </h1>
        <p className="font-josefin text-secondaryText text-sm md:text-base">
          Commence à écrire aux personnes qui comptent le plus pour toi.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <TextInput
          id="register-email"
          type="email"
          placeholder="Adresse e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <PasswordInput
          id="register-password"
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <Button type="submit" disabled={loading}>
          {loading ? "Création du compte…" : "S'inscrire"}
        </Button>
      </form>

      <div className="flex items-center gap-4 my-2">
        <div className="flex-1 h-px bg-greyLight" />
        <span className="font-josefin text-xs uppercase tracking-wider text-secondaryText">
          ou
        </span>
        <div className="flex-1 h-px bg-greyLight" />
      </div>

      <GoogleAuthButton label="S'inscrire avec Google" />
    </AuthLayout>
  );
}

export default Register;
